import type { ReactNode } from 'react';
import { Crown, Star, Flame, Zap, Shield, Rocket, Gem, Leaf, Moon, Sun, Heart, Ghost } from 'lucide-react';
import { type AvatarId, isAvatarId } from '@draughts/shared';

/**
 * Visual definition for each avatar preset key. The canonical list of keys lives
 * in @draughts/shared (AVATAR_PRESET_IDS); here we map each key to an icon and a
 * gradient used by UserAvatar and the AvatarPicker.
 */
export interface AvatarPreset {
  icon: (props: { size?: number }) => ReactNode;
  gradient: string;
}

export const AVATAR_PRESETS: Record<AvatarId, AvatarPreset> = {
  crown: { icon: (p) => <Crown {...p} />, gradient: 'linear-gradient(135deg, #F6B73C 0%, #C8860B 100%)' },
  star: { icon: (p) => <Star {...p} />, gradient: 'linear-gradient(135deg, #FFD86B 0%, #E8A317 100%)' },
  flame: { icon: (p) => <Flame {...p} />, gradient: 'linear-gradient(135deg, #FF8A5B 0%, #D8431F 100%)' },
  zap: { icon: (p) => <Zap {...p} />, gradient: 'linear-gradient(135deg, #7FD4FF 0%, #2A7FCC 100%)' },
  shield: { icon: (p) => <Shield {...p} />, gradient: 'linear-gradient(135deg, #6FA8DC 0%, #2C5F8B 100%)' },
  rocket: { icon: (p) => <Rocket {...p} />, gradient: 'linear-gradient(135deg, #B79CFF 0%, #6B3FD8 100%)' },
  gem: { icon: (p) => <Gem {...p} />, gradient: 'linear-gradient(135deg, #5BE1D4 0%, #1F9E92 100%)' },
  leaf: { icon: (p) => <Leaf {...p} />, gradient: 'linear-gradient(135deg, #8BD36F 0%, #3D8B40 100%)' },
  moon: { icon: (p) => <Moon {...p} />, gradient: 'linear-gradient(135deg, #5C6B9C 0%, #2C3458 100%)' },
  sun: { icon: (p) => <Sun {...p} />, gradient: 'linear-gradient(135deg, #FFD86B 0%, #FF8A00 100%)' },
  heart: { icon: (p) => <Heart {...p} />, gradient: 'linear-gradient(135deg, #FF8AB0 0%, #D81F5B 100%)' },
  ghost: { icon: (p) => <Ghost {...p} />, gradient: 'linear-gradient(135deg, #B8C0CC 0%, #6B7585 100%)' },
};

/**
 * Returns the preset for a stored avatar key, or null if absent/invalid (in
 * which case callers should fall back to the username initial).
 */
export function getAvatarPreset(avatar: string | null | undefined): AvatarPreset | null {
  if (!isAvatarId(avatar)) return null;
  return AVATAR_PRESETS[avatar];
}
